import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams } from 'react-router-dom'
import { db, kvGet, payOrder, saveEInvoice, type OrderEInvoice, type PayMethod } from '../lib/db'
import { defaultPrinter, printReceipt, probeAgent, type PrinterConfig } from '../lib/agent'
import { einvoiceApi, invoiceLabel, type EInvoiceBuyer } from '../lib/einvoice'
import { cashSuggestions, money, qtyToText } from '../lib/money'
import { useApp } from '../lib/store'
import { pushNow } from '../lib/sync'
import { AppBar } from '../components/AppBar'
import { BuyerSheet } from '../components/BuyerSheet'
import { NumPad } from '../components/NumPad'
import { VietQr } from '../components/VietQr'

/**
 * Hạn chờ hoá đơn điện tử, tính từ lúc bấm thu tiền.
 *
 * Quá mốc này thì thôi, in bill mà không có mã cơ quan thuế: hàng đợi trên máy chủ vẫn
 * xuất tiếp, khách cần tờ có mã thì in lại sau. Người bán không được đứng chờ mạng.
 */
const INVOICE_WAIT_MS = 9000

export function PayScreen() {
  const { orderId = '' } = useParams()
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)

  const [method, setMethod] = useState<PayMethod>('Cash')
  const [given, setGiven] = useState(0)
  const [done, setDone] = useState<{ total: number; change: number } | null>(null)
  const [printer, setPrinter] = useState<PrinterConfig | null>(null)
  const [printing, setPrinting] = useState(false)

  // ── Hoá đơn điện tử ──
  const [buyer, setBuyer] = useState<EInvoiceBuyer | null>(null)
  const [askBuyer, setAskBuyer] = useState(false)
  const [invoice, setInvoice] = useState<OrderEInvoice | null>(null)
  const [issuing, setIssuing] = useState(false)
  const eInvoiceOn = shop?.eInvoiceEnabled ?? false

  // Hỏi trước xem máy này có in được không, để nút In bill chỉ hiện khi bấm vào là có
  // giấy chạy ra. Nút bấm không làm gì cả còn tệ hơn là không có nút.
  useEffect(() => {
    void (async () => {
      const saved = await kvGet<PrinterConfig>('printer')
      if (!saved?.address) return
      if (await probeAgent()) setPrinter(saved ?? defaultPrinter)
    })()
  }, [])

  const order = useLiveQuery(() => db.orders.get(orderId), [orderId])
  if (!order) return <div className="screen" />

  const total = order.total
  const change = given - total
  const canPay = method === 'Transfer' || given >= total

  /**
   * Xin phát hành hoá đơn điện tử.
   *
   * Có hạn chờ, và hạn ấy không phải để làm đẹp: nhà cung cấp treo thì hàng người đợi trả
   * tiền cũng treo theo. Hết hạn thì bỏ qua, máy chủ vẫn xuất tiếp trong hàng đợi.
   */
  async function settleInvoice(withBuyer: EInvoiceBuyer | null) {
    if (!eInvoiceOn) return

    setIssuing(true)
    try {
      const work = (async () => {
        // Đẩy đơn lên trước: máy chủ chưa có đơn thì chưa xuất hoá đơn cho nó được.
        await pushNow()
        return await einvoiceApi.issue(orderId, withBuyer ?? undefined)
      })()

      const res = await Promise.race([
        work,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), INVOICE_WAIT_MS)),
      ])

      if (res) {
        const snapshot: OrderEInvoice = {
          status: res.status,
          templateCode: res.templateCode,
          series: res.series,
          invoiceNo: res.invoiceNo,
          taxAuthorityCode: res.taxAuthorityCode,
          lookupUrl: res.lookupUrl,
          issuedAt: res.issuedAt,
          error: res.error,
        }
        await saveEInvoice(orderId, snapshot)
        setInvoice(snapshot)
        if (res.status === 'Failed' && res.error) say(res.error)
      }
    } catch (e) {
      // Mất mạng là chuyện thường ở quán. Không báo đỏ, hàng đợi trên máy chủ lo tiếp.
      if (e instanceof Error && e.message !== 'Không có mạng') say(e.message)
    }
    setIssuing(false)
  }

  async function pay() {
    if (!order || !canPay) return
    await payOrder(order.id, method, method === 'Cash' ? given : total)
    setDone({ total: order.total, change: method === 'Cash' ? Math.max(0, given - order.total) : 0 })

    // Tự xuất mọi đơn, hoặc khách đã khai thông tin lấy hoá đơn.
    if (eInvoiceOn && (shop?.eInvoiceAutoIssue || buyer)) void settleInvoice(buyer)
    else void pushNow()
  }

  if (done) {
    const label = invoice && invoice.status === 'Issued' ? invoiceLabel(invoice) : null

    return (
      <div className="screen">
        {askBuyer && (
          <BuyerSheet
            value={buyer}
            onClose={() => setAskBuyer(false)}
            onSave={(b) => {
              setBuyer(b)
              setAskBuyer(false)
              void settleInvoice(b)
            }}
          />
        )}

        <div className="body center">
          <div className="done">
            <div className="done-mark" aria-hidden="true">
              ✓
            </div>
            <h2>Đã thu {money(done.total)}đ</h2>
            {done.change > 0 ? (
              <p className="done-change">
                Thối lại khách <b className="num">{money(done.change)}đ</b>
              </p>
            ) : (
              <p>{method === 'Cash' ? 'Khách đưa đúng tiền' : 'Đã nhận chuyển khoản'}</p>
            )}

            {eInvoiceOn && (
              <p className="note">
                {issuing
                  ? 'Đang xin mã cơ quan thuế…'
                  : label
                    ? `Hoá đơn ${label}`
                    : invoice?.status === 'Failed'
                      ? (invoice.error ?? 'Chưa xuất được hoá đơn')
                      : invoice || shop?.eInvoiceAutoIssue
                        ? 'Hoá đơn sẽ xuất xong khi máy có mạng'
                        : 'Chưa xuất hoá đơn cho đơn này'}
              </p>
            )}
          </div>
        </div>

        <div className="dock">
          {eInvoiceOn && !invoice && !issuing && (
            <button type="button" className="btn btn-ghost" onClick={() => setAskBuyer(true)}>
              🧾 Xuất hoá đơn
            </button>
          )}

          {printer && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={printing || issuing}
              onClick={async () => {
                setPrinting(true)
                try {
                  const fresh = await db.orders.get(orderId)
                  if (fresh) await printReceipt(fresh, shop, printer)
                } catch (e) {
                  say(e instanceof Error ? e.message : 'Không in được')
                }
                setPrinting(false)
              }}
            >
              {printing ? 'Đang in…' : issuing ? 'Đợi mã hoá đơn…' : 'In bill'}
            </button>
          )}
          <button type="button" className="btn btn-brand grow" onClick={() => nav('/tables')}>
            Xong
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="screen">
      {askBuyer && (
        <BuyerSheet
          value={buyer}
          onClose={() => setAskBuyer(false)}
          onSave={(b) => {
            setBuyer(b)
            setAskBuyer(false)
          }}
        />
      )}

      <AppBar
        title="Thu tiền"
        sub={`${order.tableName ?? 'Bán nhanh'} · ${qtyToText(order.items.reduce((s, i) => s + i.qtyMilli, 0))} món`}
        onBack={() => nav(`/order/${order.id}`)}
      />

      <div className="body">
        <div className="pay-total">
          <div className="pay-row big">
            <span>Khách trả</span>
            <b className="num">{money(total)}</b>
          </div>

          {method === 'Cash' && (
            <>
              <div className="pay-row">
                <span>Khách đưa</span>
                <b className="num">{given ? money(given) : '—'}</b>
              </div>
              <div className={`pay-row change${change < 0 ? ' short' : ''}`}>
                <span>{change < 0 ? 'Còn thiếu' : 'Tiền thối'}</span>
                <b className="num">{given ? money(Math.abs(change)) : '—'}</b>
              </div>
            </>
          )}
        </div>

        <div className="seg seg-lg">
          <button type="button" aria-pressed={method === 'Cash'} onClick={() => setMethod('Cash')}>
            💵 Tiền mặt
          </button>
          <button type="button" aria-pressed={method === 'Transfer'} onClick={() => setMethod('Transfer')}>
            📱 Chuyển khoản
          </button>
        </div>

        {/*
          Khai thông tin người mua TRƯỚC khi thu tiền, không phải sau.
          Hoá đơn xuất rồi thì mã số thuế người mua không sửa được nữa — phải lập hoá đơn
          thay thế, việc mà không chủ quán nhỏ nào muốn dính vào.
        */}
        {eInvoiceOn && (
          <button type="button" className="row-btn" onClick={() => setAskBuyer(true)}>
            <span className="row-icon" aria-hidden="true">
              🧾
            </span>
            <span className="row-text">
              <b>{buyer ? (buyer.name ?? 'Khách lấy hoá đơn') : 'Khách lấy hoá đơn?'}</b>
              <small>
                {buyer?.taxCode
                  ? `MST ${buyer.taxCode}`
                  : buyer
                    ? 'Đã khai thông tin người mua'
                    : shop?.eInvoiceAutoIssue
                      ? 'Đơn nào cũng có hoá đơn — bấm để khai mã số thuế công ty'
                      : 'Bấm nếu khách cần hoá đơn'}
              </small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        )}

        {method === 'Cash' ? (
          <>
            <div className="chip-row chip-cash">
              {cashSuggestions(total).map((v) => (
                <button
                  key={v}
                  type="button"
                  className="chip"
                  aria-pressed={given === v}
                  onClick={() => setGiven(v)}
                >
                  {v === total ? 'Đúng tiền' : money(v)}
                </button>
              ))}
            </div>
            <NumPad value={given} onChange={setGiven} />
          </>
        ) : (
          <VietQr shop={shop} amount={total} code={order.code} />
        )}
      </div>

      <div className="dock">
        <button type="button" className="btn btn-brand btn-lg grow" disabled={!canPay} onClick={pay}>
          {canPay ? `Xong · ${money(total)}đ` : `Còn thiếu ${money(Math.abs(change))}đ`}
        </button>
      </div>
    </div>
  )
}
